<template>
  <div class="v-templates-filter__wrapper">
    <div class="search-bar">
      <VInput
        icon="search"
        iconFABase="fal"
        iconPosition="right"
        placeholder="Search..."
        classType="primary"
        name="filterTemplates"
        :value="searchString"
        @input="onFilter"
        extraClass="templates-filter"
      ></VInput>
    </div>

    <div class="selects">
      <div class="select-item types">
        <VCheckboxSelect
          :items="_templateTypes"
          :selectedKeys="localSelectedPostcardSizes"
          :extraClass="getCheckboxSelectClass(localSelectedPostcardSizes)"
          @oncheck="key => handleCheckboxSelection(key, 'size', 'postcard')"
        >
          <template slot="title">
            <div class="select-title">
              Filter by Template (Postcards)
            </div>
          </template>
        </VCheckboxSelect>
        <VCheckboxSelect
          :items="_lettersTemplateTypes"
          :selectedKeys="localSelectedLetterSizes"
          :extraClass="getCheckboxSelectClass(localSelectedLetterSizes)"
          @oncheck="key => handleCheckboxSelection(key, 'size', 'letter')"
        >
          <template slot="title">
            <div class="select-title">
              Filter by Template (Letters)
            </div>
          </template>
        </VCheckboxSelect>
      </div>

      <div class="select-item">
        <VCheckboxSelect
          :items="_templateNames"
          :selectedKeys="localSelectedTemplateNames"
          :extraClass="getCheckboxSelectClass(localSelectedTemplateNames)"
          @oncheck="key => handleCheckboxSelection(key, 'name')"
        >
          <template slot="title">
            <div class="select-title">
              Filter by Template Name
            </div>
          </template>
        </VCheckboxSelect>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

import uniqBy from "lodash/uniqBy";
import sortBy from "lodash/sortBy";
import cloneDeep from "lodash/cloneDeep";

// components
import VInput from "@/components/VInput";
import VCheckboxSelect from "@/components/VCheckboxSelect";
import {
  getLetterType,
  getPostcardFullType,
  convertMailType,
  getLetterColor
} from "@/utils/util";

export default Vue.component("VTemplatesFilter", {
  props: {
    templates: VueTypes.array,
    letterTemplates: VueTypes.array,
    searchString: VueTypes.string.def(""),
    selectedNames: VueTypes.array,
    selectedPostcardSizes: VueTypes.array,
    selectedLetterSizes: VueTypes.array
  },

  components: {
    VInput,
    VCheckboxSelect
  },

  data() {
    return {
      localSelectedTemplateNames:
        this.selectedNames && Array.from(this.selectedNames),
      localSelectedPostcardSizes:
        this.selectedPostcardSizes && Array.from(this.selectedPostcardSizes),
      localSelectedLetterSizes:
        this.selectedLetterSizes && Array.from(this.selectedLetterSizes)
    };
  },

  methods: {
    _emitUpdate() {
      this.$emit("change", ...arguments);
    },

    _emitNameUpdate(initial = false) {
      this._emitUpdate("name", this.localSelectedTemplateNames, initial);
    },

    _emitTypeUpdate(initial = false, templateType = "postcard") {
      let types =
        templateType === "postcard"
          ? this.localSelectedPostcardSizes
          : this.localSelectedLetterSizes;

      if (templateType === "all") {
        types = [...this.localSelectedPostcardSizes, ...this.localSelectedLetterSizes]
      }

      this._emitUpdate("size", types, initial, templateType);
    },

    _toggleKey(key, arr) {
      let output = [];
      const arrCopy = cloneDeep(arr);

      if (!arr) {
        return output;
      }

      if (arrCopy.includes(key)) {
        output = arrCopy.filter(itm => itm !== key);
      } else {
        output = [...arrCopy, key];
      }

      return output;
    },

    getCheckboxSelectClass(arr) {
      return arr && arr.length === 0 ? "empty" : "";
    },

    onFilter(searchString) {
      this.$emit("search", searchString);
    },

    handleCheckboxSelection(key, checkboxType, postcardType) {
      const isLetterType = postcardType === "letter";

      const localSelectedData =
        checkboxType === "size"
          ? isLetterType
            ? this.localSelectedLetterSizes
            : this.localSelectedPostcardSizes
          : this.localSelectedTemplateNames;

      const toggleResultArray = this._toggleKey(key, localSelectedData);
      const letterKeys = this._lettersTemplateTypes.map(item => item.key);
      const postcardKeys = this._templateTypes.map(item => item.key);

      // Handle "size" checkboxes (in fact, postcard-letters types)
      if (checkboxType === "size") {
        // handle "all" key
        if (key === "all") {
          // if "all" currently selected
          // need to clear all types checkboxes on toggle
          if (this._isAllCheckboxChecked) {
            this.localSelectedPostcardSizes = [];
            this.localSelectedLetterSizes = [];
          }
          // handle "all" button click
          // pick all of the letters and postcards checkboxes
          else {
            this.localSelectedPostcardSizes = postcardKeys;
            this.localSelectedLetterSizes = letterKeys;
          }

          this._emitTypeUpdate(false, "all");
        } else {
          // "letter" type handle
          if (isLetterType) {
            this.localSelectedLetterSizes = toggleResultArray;
          }
          // "postcard" type handle
          else {
            this.localSelectedPostcardSizes = toggleResultArray;
          }

          // remove "all"
          if (this._isAllCheckboxChecked) {
            this.localSelectedPostcardSizes = this.localSelectedPostcardSizes.filter(
              item => item !== "all"
            );
          } else {
            // check if all of the checkbox selected separately
            // so "all" checkbox will be checked automatically
            if (
              [...this._lettersTemplateTypes, ...this._templateTypes].filter(
                item => item.key !== "all"
              ).length ===
              [
                ...this.localSelectedLetterSizes,
                ...this.localSelectedPostcardSizes
              ].filter(item => item !== "all").length
            ) {
              this.localSelectedPostcardSizes = [
                "all",
                ...this.localSelectedPostcardSizes
              ];
            }
          }

          this._emitTypeUpdate(false, postcardType);
        }
      } else {
        if (key === "all") {
          // if "all" selected in names array
          if (this._isNamesAllChecked) {
            this.localSelectedTemplateNames = [];
          } else {
            this.localSelectedTemplateNames = this._templateNames.map(
              item => item.key
            );
          }
        } else {
          if (this._isNamesAllChecked) {
            this.localSelectedTemplateNames = toggleResultArray.filter(
              item => item !== "all"
            );
          } else {
            // check if all of the checkbox selected separately
            // so "all" checkbox will be checked automatically
            if (toggleResultArray.length === this._templateNames.length - 1) {
              this.localSelectedTemplateNames = ["all", ...toggleResultArray];
            } else {
              this.localSelectedTemplateNames = toggleResultArray;
            }
          }
        }

        this._emitNameUpdate();
      }
    }
  },

  computed: {
    _isAllCheckboxChecked() {
      return (
        this.localSelectedPostcardSizes &&
        this.localSelectedPostcardSizes.includes("all")
      );
    },

    _isNamesAllChecked() {
      return (
        this.localSelectedTemplateNames &&
        this.localSelectedTemplateNames.includes("all")
      );
    },

    _templateNames() {
      if (
        (!this.templates || !this.templates.length) &&
        (!this.letterTemplates || !this.letterTemplates.length)
      ) {
        return [];
      }

      const formattedNames = this.templates.map(item => ({
        key: item.id,
        label: item.name
      }));

      const formattedLettersNames = this.letterTemplates.map(item => ({
        key: item.id,
        label: item.name
      }));

      if (this.searchString && this.searchString !== "") {
        return [...formattedNames, ...formattedLettersNames];
      }

      return [
        {
          key: "all",
          label: "All template names"
        },
        ...formattedNames,
        ...formattedLettersNames
      ];
    },

    _lettersTemplateTypes() {
      const formattedTypes = this.letterTemplates.map(item => ({
        key: `letter-${getLetterColor(item.color)}-${convertMailType(
          item.mail_type
        )}`,
        label: getLetterType(item.color, item.mail_type)
      }));

      const distinctTypes = uniqBy(formattedTypes, "key");

      const orderedDistinctTypes = sortBy(distinctTypes, "key");

      if (this.searchString && this.searchString !== "") {
        return orderedDistinctTypes;
      }

      return orderedDistinctTypes;
    },

    _templateTypes() {
      const formattedTypes = this.templates.map(item => ({
        key: `${item.type}-${convertMailType(item.mail_type)}`,
        label: getPostcardFullType(item.type, item.mail_type)
      }));

      if (!formattedTypes || !formattedTypes.length) {
        return formattedTypes;
      }

      const distinctTypes = uniqBy(formattedTypes, "key");
      const orderedDistinctTypes = sortBy(distinctTypes, "key");

      if (this.searchString && this.searchString !== "") {
        return orderedDistinctTypes;
      }

      // convert to v-checkbox-select format
      return [
        {
          key: "all",
          label: "All template types"
        },
        ...orderedDistinctTypes
      ];
    }
  },

  watch: {
    _templateNames(value) {
      if (value) {
        this.localSelectedTemplateNames = value.map(item => item.key);
      }
    },

    selectedPostcardSizes(value) {
      this.localSelectedPostcardSizes = value && Array.from(value);
    },

    selectedLetterSizes(value) {
      this.localSelectedLetterSizes = value && Array.from(value);
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-templates-filter";

.#{$ns} {
  &__wrapper {
    width: 100%;
    height: 352px;
    background: $white;
    box-shadow: 3px 3px 25px rgba(0, 0, 0, 0.25);
    border-radius: 5px;
    padding: 10px 10px 0 10px;

    .select-title {
      color: #1f1f1f;
      font-size: 12px;
      margin-bottom: 15px;
      font-family: Lato-Regular, sans-serif;
    }

    .selects {
      display: flex;
      margin-top: 20px;
      height: 250px;

      .select-item {
        margin-right: 50px;

        &:last-child {
          margin-right: 0;
        }

        &.types {
          overflow: auto;
          height: calc(100% + 20px);

          /* width */
          &::-webkit-scrollbar {
            display: block;
            width: 7px;
            height: 8px;
          }

          /* Track */
          &::-webkit-scrollbar-track {
            background: $primary-grey;
            border: 3px solid transparent;
            background-clip: content-box;
          }

          /* Handle */
          &::-webkit-scrollbar-thumb {
            background: #5757f5;
            border-radius: 3px;
          }

          /* Handle on hover */
          &::-webkit-scrollbar-thumb:hover {
            background: #5757f5;
          }

          /deep/ .v-checkbox-select__wrapper {
            height: auto;

            &:first-child {
              margin-bottom: 20px;
            }
          }
        }
      }
    }
  }

  .search-bar {
    width: 100%;

    /deep/ .templates-filter.primary {
      input {
        width: 100%;
      }

      .icon-wrapper i {
        font-size: 14px;
        color: #231f20;
        transform: none;
      }
    }
  }
}
</style>
